import React, { useEffect, useMemo, useState } from 'react'
import { PlusIcon, TrashIcon } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import {
  fetchContractProperties,
  fetchPropertyUnits,
  normalizeUnitNumber,
  type PropertyUnit,
} from '../../api/turnoverContracts'
import type { SelectedContractLineItem } from '../../types/job'

interface RateTier {
  id: string
  contract_service_id?: string | null
  service_item_id?: string | null
  description: string
  unit_type: string
  unit_price: number
}

export interface ApartmentTurnoverRow {
  clientId: string
  propertyId: string
  unitNumber: string
  tierId: string
  tierDescription?: string
  contractServiceId?: string | null
  serviceItemId?: string | null
  checklist: Array<{ label: string; sort_order: number; photo_required?: boolean }>
}

interface Props {
  contractId: string
  initialPropertyId?: string
  rows: ApartmentTurnoverRow[]
  onRowsChange: (rows: ApartmentTurnoverRow[]) => void
  onPropertyChange: (propertyId: string) => void
  onSelectionChange: (items: SelectedContractLineItem[], total: number) => void
  onAvailableCountChange?: (count: number) => void
}

const newRow = (propertyId: string): ApartmentTurnoverRow => ({
  clientId: globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`,
  propertyId,
  unitNumber: '',
  tierId: '',
  checklist: [],
})

export function ApartmentTurnoverEditor({
  contractId,
  initialPropertyId,
  rows,
  onRowsChange,
  onPropertyChange,
  onSelectionChange,
  onAvailableCountChange,
}: Props) {
  const [properties, setProperties] = useState<
    Array<{ id: string; name?: string | null; address?: string | null }>
  >([])
  const [tiers, setTiers] = useState<RateTier[]>([])
  const [rememberedUnits, setRememberedUnits] = useState<PropertyUnit[]>([])
  const [propertyId, setPropertyId] = useState(initialPropertyId || '')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let live = true
    const load = async () => {
      setLoading(true)
      const [links, tierResult] = await Promise.all([
        fetchContractProperties(contractId, true),
        supabase
          .from('contract_line_items')
          .select('id, contract_service_id, service_item_id, description, unit_type, unit_price')
          .eq('contract_id', contractId)
          .order('sort_order'),
      ])
      if (!live) return
      if (tierResult.error) throw tierResult.error
      const options = links.map((link) => ({
        id: link.property_id,
        name: link.property?.name,
        address: link.property?.address,
      }))
      const nextPropertyId =
        initialPropertyId && options.some((property) => property.id === initialPropertyId)
          ? initialPropertyId
          : options[0]?.id || initialPropertyId || ''
      setProperties(options)
      setTiers(tierResult.data || [])
      onAvailableCountChange?.((tierResult.data || []).length)
      setPropertyId(nextPropertyId)
      onPropertyChange(nextPropertyId)
      if (rows.length === 0 && nextPropertyId) onRowsChange([newRow(nextPropertyId)])
      setLoading(false)
    }
    load().catch(() => {
      if (live) setLoading(false)
    })
    return () => {
      live = false
    }
    // Row changes are intentionally handled independently.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId])

  useEffect(() => {
    if (!propertyId) {
      setRememberedUnits([])
      return
    }
    fetchPropertyUnits(propertyId).then(setRememberedUnits).catch(() => setRememberedUnits([]))
  }, [propertyId])

  const selection = useMemo(() => {
    const grouped = new Map<string, number>()
    for (const row of rows) {
      if (row.unitNumber.trim() && row.tierId) {
        grouped.set(row.tierId, (grouped.get(row.tierId) || 0) + 1)
      }
    }
    const items: SelectedContractLineItem[] = []
    for (const [tierId, quantity] of Array.from(grouped.entries())) {
      const tier = tiers.find((item) => item.id === tierId)
      if (!tier) continue
      items.push({
        contract_line_item_id: tier.id,
        contract_service_id: tier.contract_service_id,
        service_item_id: tier.service_item_id,
        description: tier.description,
        quantity,
        is_variable: false,
        unit_type: tier.unit_type,
        unit_price: Number(tier.unit_price || 0),
        total_price: quantity * Number(tier.unit_price || 0),
      })
    }
    return { items, total: items.reduce((sum, item) => sum + item.total_price, 0) }
  }, [rows, tiers])

  useEffect(() => {
    onSelectionChange(selection.items, selection.total)
  }, [onSelectionChange, selection])

  const updateRow = (clientId: string, changes: Partial<ApartmentTurnoverRow>) => {
    onRowsChange(rows.map((row) => (row.clientId === clientId ? { ...row, ...changes } : row)))
  }

  const updateUnitNumber = (row: ApartmentTurnoverRow, unitNumber: string) => {
    const remembered = rememberedUnits.find(
      (unit) => unit.normalized_unit_number === normalizeUnitNumber(unitNumber)
    )
    const rememberedTier = remembered?.default_rate_description
      ? tiers.find((tier) => tier.description === remembered.default_rate_description)
      : undefined
    updateRow(row.clientId, {
      unitNumber,
      tierId: rememberedTier?.id || row.tierId,
      tierDescription: rememberedTier?.description || row.tierDescription,
      contractServiceId: rememberedTier?.contract_service_id || row.contractServiceId,
      serviceItemId: rememberedTier?.service_item_id || row.serviceItemId,
      checklist: rememberedTier && rememberedTier.id !== row.tierId ? [] : row.checklist,
    })
  }

  if (loading) return <p className="text-sm text-gray-500">Loading covered buildings and rates…</p>

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3">
        <p className="text-sm font-medium text-indigo-900">Apartments in this turnover job</p>
        <p className="text-xs text-indigo-700 mt-1">
          Add each apartment separately. Its selected rate tier determines billing and its own
          checklist.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Building *</label>
        <select
          required
          value={propertyId}
          onChange={(event) => {
            const next = event.target.value
            setPropertyId(next)
            onPropertyChange(next)
            onRowsChange([newRow(next)])
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
        >
          {properties.length === 0 && <option value={initialPropertyId || ''}>Contract property</option>}
          {properties.map((property) => (
            <option key={property.id} value={property.id}>
              {property.name || property.address}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-2">
        {rows.map((row, index) => (
          <div
            key={row.clientId}
            className="grid grid-cols-1 md:grid-cols-[5rem_1fr_1fr_auto] items-end gap-2 rounded-lg border border-gray-200 p-3"
          >
            <span className="text-sm font-medium text-gray-500 md:self-center">
              Apt {index + 1}
            </span>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Apartment number *</label>
              <input
                list={`remembered-units-${contractId}`}
                value={row.unitNumber}
                onChange={(event) => updateUnitNumber(row, event.target.value)}
                placeholder="e.g. 4B"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Rate tier *</label>
              <select
                value={row.tierId}
                onChange={(event) => {
                  const tier = tiers.find((item) => item.id === event.target.value)
                  updateRow(row.clientId, {
                    tierId: event.target.value,
                    tierDescription: tier?.description,
                    contractServiceId: tier?.contract_service_id,
                    serviceItemId: tier?.service_item_id,
                    checklist: [],
                  })
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">Select rate…</option>
                {tiers.map((tier) => (
                  <option key={tier.id} value={tier.id}>
                    {tier.description} — ${Number(tier.unit_price).toFixed(2)}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              disabled={rows.length === 1}
              onClick={() => onRowsChange(rows.filter((item) => item.clientId !== row.clientId))}
              className="p-2 text-red-600 disabled:text-gray-300"
              aria-label={`Remove apartment ${index + 1}`}
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        ))}
        <datalist id={`remembered-units-${contractId}`}>
          {rememberedUnits.map((unit) => (
            <option key={unit.id} value={unit.unit_number}>
              {unit.default_rate_description || ''}
            </option>
          ))}
        </datalist>
      </div>

      <button
        type="button"
        onClick={() => onRowsChange([...rows, newRow(propertyId)])}
        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
      >
        <PlusIcon className="w-4 h-4" />
        Add apartment
      </button>
      <p className="text-sm font-medium text-gray-900 text-right">
        Planned total: ${selection.total.toFixed(2)}
      </p>
    </div>
  )
}
